import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { filter } from 'rxjs';

import { TranslateModule } from '@ngx-translate/core';

import { type DynamicAction } from '@shared/registry';

import { type TabsConfig } from './tabs.types';

@Component({
  selector: 'app-tabs',
  templateUrl: './tabs.html',
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Tabs {
  private readonly router = inject(Router);

  readonly config = input.required<TabsConfig[]>();
  readonly disabled = input<boolean>(false);

  readonly tabClick = output<DynamicAction>();

  private readonly currentPath = signal<string>(this.getCurrentPath());

  protected readonly tabs = computed(() => {
    const path = this.currentPath();

    return this.config()
      .filter((tab) => !tab.hide)
      .map((tab) => {
        const isRoutePath = tab.route.startsWith('/');
        const isActive = isRoutePath ? path === tab.route || path.startsWith(tab.route + '/') : tab.isActive;

        return { ...tab, isActive };
      });
  });

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.currentPath.set(this.getCurrentPath()));
  }

  protected onTabClick(tab: TabsConfig): void {
    if (tab.action) {
      this.tabClick.emit({ ...tab.action, data: tab.route });
    }
  }

  private getCurrentPath(): string {
    return this.router.url.split('?')[0];
  }
}
